import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Filter, Shield } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

function fmtDT(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const CONV_TYPE_LABEL = {
    dm: "DM misto",
    internal_thread: "Interno staff",
    client_thread: "Canale cliente",
    ticket: "Ticket",
};

export default function AdminAccessLog() {
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (user && !user.is_super_admin) {
            toast.error("Accesso riservato ai super admin");
            navigate("/app");
        }
    }, [user, navigate]);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sinceMin, setSinceMin] = useState(""); // "" = all-time

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (sinceMin) params.since_minutes = Number(sinceMin);
            const { data } = await api.get("/admin/super-admin-access-log", { params });
            setItems(data.items || []);
        } catch { toast.error("Errore caricamento log accessi"); }
        finally { setLoading(false); }
    }, [sinceMin]);

    useEffect(() => { load(); }, [load]);

    if (user && !user.is_super_admin) return null;

    return (
        <div style={{ padding: "32px 40px", maxWidth: 1120 }} data-testid="admin-access-log-page">
            <div className="flex items-center gap-3" style={{ marginBottom: 8 }}>
                <Shield size={20} color="#181B20" />
                <h1 className="lt-h1" data-testid="admin-access-log-heading">Log accessi super admin</h1>
            </div>
            <p className="lt-sub" style={{ marginTop: 4, marginBottom: 20, maxWidth: 720 }}>
                Registro immutabile delle aperture di conversazioni da parte di super admin. Utile per trasparenza operativa e audit.
            </p>

            <Link to="/app/admin/conversazioni" className="lt-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#5F6560", marginBottom: 16 }} data-testid="admin-access-log-back">
                <ArrowLeft size={14} /> Torna alle conversazioni
            </Link>

            <div className="lt-card" style={{ padding: 16, marginBottom: 20 }}>
                <div className="flex items-center gap-3">
                    <Filter size={14} color="#5F6560" />
                    <select
                        className="lt-input"
                        value={sinceMin}
                        onChange={(e) => setSinceMin(e.target.value)}
                        data-testid="admin-access-log-since"
                        style={{ minWidth: 200 }}
                    >
                        <option value="">Tutti (max 200)</option>
                        <option value="60">Ultima ora</option>
                        <option value="1440">Ultime 24 ore</option>
                        <option value="10080">Ultima settimana</option>
                        <option value="43200">Ultimo mese</option>
                    </select>
                    <div style={{ marginLeft: "auto", fontSize: 13, color: "#5F6560" }} data-testid="admin-access-log-count">
                        {loading ? "Caricamento…" : `${items.length} eventi`}
                    </div>
                </div>
            </div>

            <div className="lt-card lt-card--scroll-x" data-testid="admin-access-log-table-wrap">
                {items.length === 0 && !loading ? (
                    <div style={{ padding: 32, textAlign: "center", color: "#8A8F88" }}>Nessun accesso registrato.</div>
                ) : (
                    <table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse" }} data-testid="admin-access-log-table">
                        <thead>
                            <tr style={{ background: "#F4F5F2", fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#5F6560" }}>
                                <th style={{ textAlign: "left", padding: "10px 14px" }}>Timestamp</th>
                                <th style={{ textAlign: "left", padding: "10px 14px" }}>Super admin</th>
                                <th style={{ textAlign: "left", padding: "10px 14px" }}>Tipo</th>
                                <th style={{ textAlign: "left", padding: "10px 14px" }}>Conv. ID</th>
                                <th style={{ textAlign: "left", padding: "10px 14px" }}>Org</th>
                                <th style={{ textAlign: "left", padding: "10px 14px" }}>IP</th>
                                <th style={{ textAlign: "left", padding: "10px 14px" }}>Motivo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((e) => (
                                <tr key={e.correlation_id + e.timestamp} data-testid={`admin-access-log-row-${e.correlation_id}`} style={{ borderTop: "1px solid #EEEFEA", fontSize: 13, color: "#3A3F3A" }}>
                                    <td style={{ padding: "10px 14px" }}>{fmtDT(e.timestamp)}</td>
                                    <td style={{ padding: "10px 14px", fontFamily: '"IBM Plex Mono", monospace', fontSize: 12 }}>{e.super_admin_email_masked}</td>
                                    <td style={{ padding: "10px 14px" }}>
                                        <span className="lt-badge" style={{ fontSize: 10, background: "#F4F5F2", color: "#5F6560", padding: "2px 6px", borderRadius: 3, textTransform: "uppercase", letterSpacing: "0.05em", fontFamily: '"IBM Plex Mono", monospace' }}>
                                            {CONV_TYPE_LABEL[e.conversation_type] || e.conversation_type}
                                        </span>
                                    </td>
                                    <td style={{ padding: "10px 14px", fontFamily: '"IBM Plex Mono", monospace', fontSize: 12, color: "#5F6560" }}>{e.conversation_id.slice(0, 12)}…</td>
                                    <td style={{ padding: "10px 14px" }}>{e.org_scope === "internal" ? "ENERLEFT Staff" : e.org_scope.slice(0, 10) + "…"}</td>
                                    <td style={{ padding: "10px 14px", fontFamily: '"IBM Plex Mono", monospace', fontSize: 12, color: "#8A8F88" }}>{e.ip_masked || "—"}</td>
                                    <td style={{ padding: "10px 14px", fontSize: 12, color: "#5F6560" }}>{e.access_reason || "routine_audit"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
